// Shared checkout utility for camera pack pages
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metadata for Meta Pixel InitiateCheckout events. Prices in USD.
export const PRODUCT_META = {
  'full-pack':       { name: 'SOLARIS Full Pack',       price: 49 },
  'sony-pack':       { name: 'SOLARIS Sony Pack',       price: 24 },
  'arri-pack':       { name: 'SOLARIS ARRI Pack',       price: 24 },
  'red-pack':        { name: 'SOLARIS RED Pack',        price: 24 },
  'blackmagic-pack': { name: 'SOLARIS Blackmagic Pack', price: 24 },
  'canon-pack':      { name: 'SOLARIS Canon Pack',      price: 24 },
  'panasonic-pack':  { name: 'SOLARIS Panasonic Pack',  price: 24 },
  'apple-pack':      { name: 'SOLARIS Apple Pack',      price: 24 },
  'standard-pack':   { name: 'SOLARIS Standard Pack',   price: 24 },
};

// Fire Meta Pixel InitiateCheckout + AddToCart + Microsoft Clarity buy_click event.
// AddToCart + InitiateCheckout are fired together (single-page checkout flow —
// there's no separate cart step). This gives Meta two learning signals on
// buy click without a real cart page. Keep this pure — callers decide when to redirect.
export const trackInitiateCheckout = (productId, priceOverride = null) => {
  if (typeof window === 'undefined') return;
  const meta = PRODUCT_META[productId] || { name: productId, price: 0 };
  const price = priceOverride != null ? priceOverride : meta.price;
  const payload = {
    value: price,
    currency: 'USD',
    content_name: meta.name,
    content_ids: [productId],
    content_type: 'product',
  };

  // Stash the intent so /success can fire Purchase IMMEDIATELY on mount with
  // accurate revenue + sku (before the backend polling round-trip completes).
  // Guards against Meta missing the conversion if user closes tab in the ~1-2s
  // polling window after Stripe redirects them to /success.
  try {
    sessionStorage.setItem(
      'solaris_last_checkout',
      JSON.stringify({ sku: productId, price, name: meta.name, ts: Date.now() })
    );
  } catch (e) {
    // sessionStorage may be blocked (Safari private mode) — non-fatal.
  }

  // Meta Pixel — AddToCart first (mid-funnel signal), then InitiateCheckout
  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'AddToCart', payload);
      window.fbq('track', 'InitiateCheckout', payload);
      _lastAddToCartTs = Date.now(); // suppress global listener double-fire
    } catch (e) {
      console.warn('fbq buy-click events failed:', e);
    }
  }

  // Microsoft Clarity — tags the session so we can filter recordings
  // to buy-clickers only (and slice heatmaps by sku).
  if (typeof window.clarity === 'function') {
    try {
      window.clarity('event', 'buy_click');
      window.clarity('set', 'sku', productId);
    } catch (e) {
      console.warn('clarity buy_click failed:', e);
    }
  }
};

/**
 * Read (and clear) the last-checkout intent stashed by trackInitiateCheckout.
 * Used by /success to fire Purchase immediately on mount with correct sku/value
 * without waiting for the backend polling round-trip. Returns null if no stash
 * or stash is stale (>1 hour) — in that case caller should fall back to defaults.
 */
export const readAndClearStashedCheckoutIntent = () => {
  try {
    const raw = sessionStorage.getItem('solaris_last_checkout');
    if (!raw) return null;
    sessionStorage.removeItem('solaris_last_checkout');
    const parsed = JSON.parse(raw);
    // Stale intents (>1h) shouldn't attribute to a fresh purchase.
    if (!parsed.ts || Date.now() - parsed.ts > 3600 * 1000) return null;
    return parsed; // { sku, price, name, ts }
  } catch (e) {
    return null;
  }
};

// Fire ViewContent when a product surface enters the viewport. Meta uses this
// as a warm-audience signal separate from raw PageView.
// Guarded to fire at most once per (page-load, productId) via a module-level Set.
const _viewContentFiredFor = new Set();
export const trackViewContent = (productId) => {
  if (typeof window === 'undefined') return;
  if (_viewContentFiredFor.has(productId)) return;
  _viewContentFiredFor.add(productId);
  const meta = PRODUCT_META[productId] || { name: productId, price: 0 };
  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', 'ViewContent', {
        value: meta.price,
        currency: 'USD',
        content_name: 'SOLARIS LUT Pack',
        content_ids: [productId],
        content_type: 'product',
      });
    } catch (e) {
      console.warn('fbq ViewContent failed:', e);
    }
  }
};

// Timestamp of last AddToCart (from any source). Used by both the checkout util
// AND the global button-text listener to prevent double-fires within 500ms.
let _lastAddToCartTs = 0;

// Global document-level click listener: fires AddToCart on ANY button whose
// text expresses buying intent ($49, $24, "pack", "buy"). This catches
// scroll-anchor CTAs and secondary buttons that don't call trackInitiateCheckout
// directly. Debounced so real checkout clicks (which also call
// trackInitiateCheckout) don't produce duplicate AddToCart events.
export const installGlobalBuyClickTracker = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  if (window.__solarisBuyClickTracker) return window.__solarisBuyClickTracker;

  const priceRegex = /\$(\d+(?:\.\d+)?)/;
  // eslint-disable-next-line no-useless-escape
  const intentRegex = /(pack|buy|\$\d)/i;

  const onClick = (e) => {
    // Walk up the DOM to find the closest button/anchor that was clicked.
    const btn = e.target && e.target.closest ? e.target.closest('button, a, [role="button"]') : null;
    if (!btn) return;
    const text = (btn.innerText || btn.textContent || '').trim();
    if (!text || text.length > 120) return;
    if (!intentRegex.test(text)) return;

    // Debounce vs. explicit trackInitiateCheckout calls (same tick or within 500ms).
    if (Date.now() - _lastAddToCartTs < 500) return;
    _lastAddToCartTs = Date.now();

    const m = text.match(priceRegex);
    const value = m ? parseFloat(m[1]) : 49;

    if (typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'AddToCart', {
          value,
          currency: 'USD',
          content_name: 'SOLARIS LUT Pack',
        });
      } catch (err) {
        console.warn('fbq AddToCart (global) failed:', err);
      }
    }
  };

  document.addEventListener('click', onClick, false); // bubble phase — runs AFTER React onClick
  const cleanup = () => {
    document.removeEventListener('click', onClick, false);
    // Clear the cached tracker so a subsequent install() re-attaches.
    // Without this, React StrictMode's mount → cleanup → mount cycle in dev
    // leaves the listener detached (second install() early-returns on the cached flag).
    if (window.__solarisBuyClickTracker === cleanup) {
      delete window.__solarisBuyClickTracker;
    }
  };
  window.__solarisBuyClickTracker = cleanup;
  return cleanup;
};

/**
 * Called synchronously from a real-checkout button onClick BEFORE any async
 * work (fetch, await). Prevents the global bubble-phase click listener from
 * firing a redundant AddToCart on the same click event.
 */
export const markCheckoutIntent = () => {
  _lastAddToCartTs = Date.now();
};

// Delay in ms between firing the pixel event and navigating away.
// Firing + redirecting in the same tick is the #1 reason these events
// silently never arrive — the pixel POST gets killed by page unload.
const PIXEL_FLUSH_DELAY_MS = 250;

export const initiateCheckout = async (productId, setIsLoading, discountCode = null) => {
  // Mark checkout intent synchronously so the global bubble-phase click
  // listener (which fires AFTER this handler) skips its redundant AddToCart.
  markCheckoutIntent();
  setIsLoading(true);

  try {
    const response = await fetch(`${API_URL}/api/checkout/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product_id: productId,
        origin_url: window.location.origin,
        discount_code: discountCode || undefined,
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const data = await response.json();

    if (data.checkout_url) {
      // Fire pixel BEFORE redirect, then give it 250ms to actually flush.
      // If a discountCode is present (e.g. FLASH50), reflect the discounted
      // price by reading the amount from the server's response if provided.
      const priceOverride =
        typeof data.amount === 'number' ? data.amount :
        typeof data.discounted_price === 'number' ? data.discounted_price :
        null;
      trackInitiateCheckout(productId, priceOverride);
      setTimeout(() => {
        window.location.href = data.checkout_url;
      }, PIXEL_FLUSH_DELAY_MS);
      // Intentionally NOT clearing loading state — we're about to unload.
      return;
    }
  } catch (error) {
    console.error('Checkout error:', error);
    alert('Unable to start checkout. Please try again.');
    setIsLoading(false);
  }
};

export const PRODUCT_IDS = {
  'Sony': 'sony-pack',
  'ARRI': 'arri-pack',
  'RED': 'red-pack',
  'Blackmagic': 'blackmagic-pack',
  'Canon': 'canon-pack',
  'Panasonic': 'panasonic-pack',
  'Apple': 'apple-pack',
  'Standard': 'standard-pack'
};
